// pages/ViewGreetingCard.js - a greeting card, opened from its envelope.
//
// A port of the app's CardDetailScreen
// (invits-app-new/src/features/cards/screens/CardDetailScreen.js): the same
// background rules, the same envelope - paper, ribbon, bow, flaps, pocket - in
// the card's own envelope style, the same cover, and the same two-second
// opening, driven from one progress value through the app's own interpolation
// curves so the web opening is the app's opening.
//
// What was here before was a generic stand-in. It painted `theme_color` as the
// only background (no theme image, no theme video, no blurred cover), drew a
// plain box for the envelope, and lifted the card a fixed 100px from the top
// of a centred stage - which on most windows carried it up over the "From"
// row and left it there. The geometry below is measured from the stage the
// card actually has, so it cannot leave it.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePageTitle } from '../utils/pageTitle';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { FiShare2, FiEdit2, FiMail, FiLock, FiPlusCircle } from 'react-icons/fi';
import { getGreetingCardBySlug } from '../services/greetingCardService';
import { firstMediaUrl } from '../utils/mediaUrl';
import { getFontById } from '../constants/cardFonts';
import { ENVELOPE_STYLES, getEnvelopeStyleById } from '../constants/envelopeStyles';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import { cardUrl } from '../shared/shortLinks';
import { parseBackgroundMusic } from '../shared/backgroundMusic';
import useLoopingAudio from '../hooks/useLoopingAudio';
import { track } from '../services/track';
import { EV } from '../shared/analyticsEvents';
import '../styles/ViewGreetingCard.scss';

// The app's default card backgrounds, in the app's order.
const DEFAULT_BACKGROUNDS = [
  '/images/backgrounds/4.jpg',
  '/images/backgrounds/1.jpg',
  '/images/backgrounds/2.jpg',
  '/images/backgrounds/3.jpg',
];

const ENVELOPE_HEIGHT = 180;
const OPEN_DURATION = 2; // seconds, as in the app
const OPEN_EASE = [0.33, 1, 0.68, 1]; // Easing.bezier(0.33, 1, 0.68, 1)
const SOUND_DELAY_MS = 800; // on the flap, not the tap
const MUSIC_DELAY_MS = 1300; // as the card comes out of the envelope

/** Reanimated's interpolate with Extrapolation.CLAMP. */
function interpolate(p, input, output) {
  if (p <= input[0]) return output[0];
  for (let i = 1; i < input.length; i += 1) {
    if (p <= input[i]) {
      const t = (p - input[i - 1]) / (input[i] - input[i - 1] || 1);
      return output[i - 1] + (output[i] - output[i - 1]) * t;
    }
  }
  return output[output.length - 1];
}

/** The cover the card shows. GIFs animate here - a browser can. */
function coverUrlFor(card) {
  if (!card) return null;
  switch (card.cover_image_type) {
    case 'gif':
      return firstMediaUrl(card.cover_gif_cdn_url, card.cover_static_gif_cdn_url, card.cover_image_cdn_url);
    case 'pdf':
      return firstMediaUrl(card.pdf_page_images?.[0]?.cdn_url, card.cover_image_cdn_url);
    case 'youtube':
      return firstMediaUrl(card.youtube_thumbnail_cdn_url, card.cover_thumbnail_cdn_url, card.cover_image_cdn_url);
    default:
      return firstMediaUrl(card.cover_image_cdn_url, card.cover_thumbnail_cdn_url);
  }
}

/**
 * What the background is blurred from. For a GIF, the GIF itself: a browser's
 * CSS blur runs on every frame, so the background moves - as the invitation
 * page's already does, and as the app's now does through Skia.
 */
function backgroundStillFor(card) {
  if (!card) return DEFAULT_BACKGROUNDS[0];
  if (card.cover_image_type === 'gif') {
    const moving = firstMediaUrl(card.cover_gif_cdn_url, card.cover_static_gif_cdn_url, card.cover_image_cdn_url);
    if (moving) return moving;
  }
  const cover = coverUrlFor(card);
  if (cover) return cover;
  const hash = (card.id || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return DEFAULT_BACKGROUNDS[hash % DEFAULT_BACKGROUNDS.length];
}

const ViewGreetingCard = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [card, setCard] = useState(null);
  // Tab title: "Happy birthday Ritu | invits - Create beautiful invitations".
  usePageTitle(card?.title);
  const [creator, setCreator] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | denied | missing | error
  const [isOpen, setIsOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStatus('loading');
        const result = await getGreetingCardBySlug(slug);
        if (cancelled) return;
        if (!result || result.accessDenied) { setStatus('denied'); return; }
        if (!result.card) { setStatus('missing'); return; }

        setCard(result.card);
        setStatus('ready');

        const { data: creatorData } = await supabase
          .from('users')
          .select('id, first_name, last_name, profile_photo_cdn_url, avatar_config')
          .eq('id', result.card.creator_id)
          .maybeSingle();
        if (!cancelled && creatorData) setCreator(creatorData);
      } catch (error) {
        console.error('Error loading card:', error);
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Counted once per card, after sign-in has settled. It was recorded inside
  // the card load, which could finish before auth did - so a sender opening
  // their own card could be counted as a recipient.
  const viewTrackedRef = useRef(null);
  useEffect(() => {
    if (!card || authLoading || viewTrackedRef.current === card.id) return;
    viewTrackedRef.current = card.id;
    track(EV.CARD_VIEWED, { as: user && card.creator_id === user.id ? 'creator' : 'recipient' });
  }, [card, user, authLoading]);

  const isCreator = !!user && !!card && card.creator_id === user.id;

  // ── Stage geometry ──────────────────────────────────────────────────────
  //
  // Everything the animation moves is measured against the stage - the space
  // between the sender row and the footer - rather than the window, so the
  // card lands in the middle of the space it has and never over the sender.
  const stageRef = useRef(null);
  const [stage, setStage] = useState({ w: 360, h: 480 });
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => setStage({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [status]);

  const envW = Math.max(200, Math.min(stage.w - 80, 280));
  const envH = ENVELOPE_HEIGHT;
  const cardSize = envW * 0.88;
  // Where the envelope drops to: most of the way down the stage, a quarter
  // of it clipped by the bottom edge, as in the app.
  const drop = Math.max(60, stage.h / 2 - envH * 0.25);
  // The card's centre at rest, relative to the stage centre (it sits 20px
  // into the envelope), and the lift that brings it to the stage centre.
  const cardRestCentre = -envH / 2 + 20 + cardSize / 2;
  const lift = -cardRestCentre;
  // 1.15x as in the app, unless the stage is too short for it.
  const cardScale = Math.max(0.6, Math.min(1.15, (stage.h - 32) / cardSize));

  const geo = useRef({});
  geo.current = { drop, lift, cardScale };

  // ── One progress value, the app's curves ────────────────────────────────
  const progress = useMotionValue(0);
  const g = (fn) => (p) => fn(p, geo.current);

  const envY = useTransform(progress, g((p, { drop: B }) =>
    interpolate(p, [0, 0.35, 0.7, 0.9, 1], [0, B, B, B + 200, B + 400])));
  const envRotateY = useTransform(progress, (p) => interpolate(p, [0, 0.35], [180, 0]));
  const envScale = useTransform(progress, (p) => interpolate(p, [0, 0.15, 0.35], [1, 0.95, 1]));
  const envOpacity = useTransform(progress, (p) => interpolate(p, [0.7, 0.9, 1], [1, 1, 0]));

  const flapOpacity = useTransform(progress, (p) => interpolate(p, [0, 0.35, 0.36], [0, 0, 1]));
  const topFlapRotateX = useTransform(progress, (p) => interpolate(p, [0.4, 0.65], [0, -180]));

  const ribbonOpacity = useTransform(progress, (p) => interpolate(p, [0, 0.25, 0.35], [1, 1, 0]));
  const ribbonScale = useTransform(progress, (p) => interpolate(p, [0.25, 0.35], [1, 0.8]));

  const cardY = useTransform(progress, g((p, { drop: B, lift: L }) =>
    interpolate(p, [0, 0.35, 0.4, 0.5, 0.75, 1], [B, B, B, B - 30, L, L])));
  const cardScaleMV = useTransform(progress, g((p, { cardScale: S }) =>
    interpolate(p, [0, 0.4, 0.5, 0.75, 1], [1, 1, 1, S, S])));
  const cardOpacity = useTransform(progress, (p) => interpolate(p, [0.45, 0.55, 0.7], [0, 0.5, 1]));

  // A resize after opening moves the card to the new resting place.
  useEffect(() => {
    if (isOpen) { progress.set(0.9999); progress.set(1); }
  }, [drop, lift, cardScale, isOpen, progress]);

  // ── Background music ────────────────────────────────────────────────────
  //
  // No controls, as in the app: it starts as the card comes out of the
  // envelope and loops the host's chosen part. The tap that opens the card is
  // what lets the browser play it - unlock() runs inside that tap.
  const cardMusic = React.useMemo(
    () => parseBackgroundMusic(card?.background_music),
    [card?.background_music],
  );
  const [musicStarted, setMusicStarted] = useState(false);
  const musicTimer = useRef(null);
  useEffect(() => () => clearTimeout(musicTimer.current), []);
  const music = useLoopingAudio(cardMusic, { playing: musicStarted, volume: 0.7, fadeMs: 1800 });

  // ── Sound ───────────────────────────────────────────────────────────────
  //
  // Safari only lets a page play sound it started inside a tap, and this one
  // is meant for the flap, 0.8s later. So the tap plays it muted - which
  // unlocks the element - and the real play follows on the flap.
  const audioRef = useRef(null);
  const soundTimer = useRef(null);
  useEffect(() => () => clearTimeout(soundTimer.current), []);

  const playSoundOnFlap = () => {
    const a = audioRef.current;
    if (!a) return;
    const playNow = () => {
      a.muted = false;
      a.currentTime = 0;
      a.play().catch(() => {});
    };
    a.muted = true;
    a.play()
      .then(() => {
        a.pause();
        soundTimer.current = setTimeout(playNow, SOUND_DELAY_MS);
      })
      .catch(() => { soundTimer.current = setTimeout(playNow, SOUND_DELAY_MS); });
  };

  const openCard = useCallback(() => {
    if (isOpen || opening) return;
    setOpening(true);
    track(EV.CARD_OPENED);
    if (navigator.vibrate) navigator.vibrate(10);
    playSoundOnFlap();
    if (cardMusic) {
      music.unlock();
      clearTimeout(musicTimer.current);
      musicTimer.current = setTimeout(() => setMusicStarted(true), MUSIC_DELAY_MS);
    }
    animate(progress, 1, {
      duration: OPEN_DURATION,
      ease: OPEN_EASE,
      onComplete: () => { setIsOpen(true); setOpening(false); },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, opening, progress, cardMusic, music.unlock]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const senderName = (() => {
    if (!creator) return 'Someone';
    return `${creator.first_name || ''} ${creator.last_name || ''}`.trim() || 'Someone';
  })();

  const handleShare = async () => {
    const url = cardUrl(card?.shareable_slug || slug, window.location.origin);
    if (navigator.share) {
      navigator.share({ title: card.title, text: `A card for you: ${card.title}`, url }).catch(() => {});
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link', url);
    }
  };

  // ── States ──────────────────────────────────────────────────────────────
  if (status !== 'ready' || !card) {
    const messages = {
      loading: null,
      denied: { icon: <FiLock />, title: 'Private card', text: "You don't have permission to view this card." },
      missing: { icon: <FiMail />, title: 'Card not found', text: 'This greeting card does not exist.' },
      error: { icon: <FiMail />, title: 'Could not load this card', text: 'Check your connection and try again.' },
    };
    const m = messages[status];
    return (
      <div className="vgc vgc--center">
        {status === 'loading' ? (
          <>
            <div className="vgc-spinner" />
            <p className="vgc-muted">Loading card…</p>
          </>
        ) : (
          <>
            <span className="vgc-state-icon">{m.icon}</span>
            <h2>{m.title}</h2>
            <p className="vgc-muted">{m.text}</p>
            <button type="button" className="vgc-btn vgc-btn--glass" onClick={() => navigate('/home')}>
              Go home
            </button>
          </>
        )}
      </div>
    );
  }

  const env = card.envelope_style ? getEnvelopeStyleById(card.envelope_style) : ENVELOPE_STYLES[0];
  const font = card.font_family ? getFontById(card.font_family) : null;
  const fontStyle = font && font.id !== 'default' ? { fontFamily: font.fontFamily } : {};
  const cover = coverUrlFor(card);

  // ── Background: the app's order ─────────────────────────────────────────
  const renderBackground = () => {
    if (card.theme_type === 'video' && card.theme_video_cdn_url) {
      return (
        <>
          {card.theme_color && <div className="vgc-bg-fill" style={{ background: card.theme_color }} />}
          <video
            className="vgc-bg-media"
            src={card.theme_video_cdn_url}
            poster={card.theme_thumbnail_cdn_url || undefined}
            autoPlay
            muted
            loop
            playsInline
          />
        </>
      );
    }
    if (card.theme_color) {
      return (
        <>
          <div className="vgc-bg-fill" style={{ background: card.theme_color }} />
          <div className="vgc-bg-dim" />
        </>
      );
    }
    const src = card.theme_image_cdn_url || backgroundStillFor(card);
    return (
      <>
        <img className="vgc-bg-media is-blurred" src={src} alt="" />
        <div className="vgc-bg-dim" />
      </>
    );
  };

  const half = envW / 2;

  return (
    <div className="vgc">
      <div className="vgc-bg" aria-hidden="true">{renderBackground()}</div>

      {/* Hidden audio, unlocked by the tap - see playSoundOnFlap. */}
      <audio ref={audioRef} src="/sounds/paper-open.mp3" preload="auto" />

      {isCreator && (
        <button
          type="button"
          className="vgc-edit"
          onClick={() => navigate(`/edit-greeting-card/${card.id}`)}
          aria-label="Edit card"
        >
          <FiEdit2 />
        </button>
      )}

      {/* From */}
      <header className="vgc-sender">
        <p className="vgc-from">From</p>
        {creator?.profile_photo_cdn_url ? (
          <img className="vgc-avatar" src={creator.profile_photo_cdn_url} alt="" />
        ) : (
          <span className="vgc-avatar vgc-avatar--initial">{senderName.charAt(0).toUpperCase()}</span>
        )}
        <h1 className="vgc-sender-name">{senderName}</h1>
      </header>

      {/* The stage: the envelope, then the card */}
      <main
        ref={stageRef}
        className={`vgc-stage${isOpen || opening ? '' : ' is-closed'}`}
        onClick={openCard}
        onKeyDown={(e) => {
          if (!isOpen && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openCard(); }
        }}
        role={isOpen ? undefined : 'button'}
        tabIndex={isOpen ? undefined : 0}
        aria-label={isOpen ? undefined : 'Open card'}
      >
        <div className="vgc-envelope-box" style={{ width: envW, height: envH }}>
          <motion.div
            className="vgc-envelope"
            style={{
              y: envY,
              rotateY: envRotateY,
              scale: envScale,
              opacity: envOpacity,
              transformPerspective: 1400,
            }}
          >
            <div
              className="vgc-envelope-body"
              style={{ background: `linear-gradient(135deg, ${env.bodyColors.join(', ')})` }}
            >
              <div className="vgc-paper" />
            </div>
            <div className="vgc-envelope-border" style={{ borderColor: env.borderColor }} />

            <motion.div
              className="vgc-ribbon vgc-ribbon--v"
              style={{ background: env.ribbonColor, opacity: ribbonOpacity, scale: ribbonScale }}
            />
            <motion.div
              className="vgc-ribbon vgc-ribbon--h"
              style={{ background: env.ribbonColor, opacity: ribbonOpacity, scale: ribbonScale }}
            />
            <motion.div className="vgc-bow" style={{ opacity: ribbonOpacity, scale: ribbonScale }}>
              <div className="vgc-bow-knot" style={{ background: env.bowColor, borderColor: env.ribbonColor }}>
                <span className="vgc-bow-loop is-left" style={{ background: env.bowColor }} />
                <span className="vgc-bow-loop is-right" style={{ background: env.bowColor }} />
                <span className="vgc-bow-shine" />
              </div>
            </motion.div>

            <motion.div className="vgc-flap vgc-flap--bottom" style={{ opacity: flapOpacity }}>
              <span
                className="vgc-triangle"
                style={{
                  bottom: 0,
                  borderLeftWidth: half,
                  borderRightWidth: half,
                  borderBottomWidth: envH * 0.5,
                  borderBottomColor: env.bottomFlapColor,
                }}
              />
            </motion.div>
            <motion.div className="vgc-flap vgc-flap--pocket" style={{ opacity: flapOpacity }}>
              <span
                className="vgc-triangle"
                style={{
                  bottom: 0,
                  borderLeftWidth: half,
                  borderRightWidth: half,
                  borderBottomWidth: envH * 0.4,
                  borderBottomColor: '#1a1a2e',
                }}
              />
            </motion.div>
            <motion.div
              className="vgc-flap vgc-flap--top"
              style={{ opacity: flapOpacity, rotateX: topFlapRotateX, transformPerspective: 1400 }}
            >
              <span
                className="vgc-triangle"
                style={{
                  top: 0,
                  borderLeftWidth: half,
                  borderRightWidth: half,
                  borderTopWidth: envH * 0.5,
                  borderTopColor: env.topFlapColor,
                }}
              />
            </motion.div>
          </motion.div>

          <motion.div
            className="vgc-card"
            style={{
              width: cardSize,
              height: cardSize,
              left: (envW - cardSize) / 2,
              y: cardY,
              scale: cardScaleMV,
              opacity: cardOpacity,
            }}
          >
            {cover ? (
              <img src={cover} alt={card.title} />
            ) : (
              <span className="vgc-card-placeholder"><FiMail /></span>
            )}
          </motion.div>
        </div>

        {!isOpen && !opening && <p className="vgc-tap">Tap to open!</p>}
      </main>

      {/* Title, message and actions. Always laid out - invisible until the
          card is open - so opening it never reflows the stage the card is
          animating in. */}
      <footer className={`vgc-footer${isOpen ? ' is-open' : ''}`} aria-hidden={!isOpen}>
        <div className="vgc-details">
          <h2 style={fontStyle}>{card.title}</h2>
          {card.notes && <p style={fontStyle}>{card.notes}</p>}
        </div>
        <div className="vgc-actions">
          <button type="button" className="vgc-btn vgc-btn--dark" onClick={handleShare} tabIndex={isOpen ? 0 : -1}>
            <FiShare2 /> {copied ? 'Link copied' : 'Share'}
          </button>
          <button
            type="button"
            className="vgc-btn vgc-btn--primary"
            onClick={() => navigate('/create-greeting-card')}
            tabIndex={isOpen ? 0 : -1}
          >
            <FiPlusCircle /> Create
          </button>
        </div>
      </footer>

    </div>
  );
};

export default ViewGreetingCard;
